/**
 * 초대코드 생성과 정규화.
 *
 * Crockford Base32를 쓴다. I·L·O·U를 뺀 32글자라 손으로 옮겨 적거나
 * 메신저로 불러줄 때 헷갈릴 여지가 적다.
 */

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
const CODE_LENGTH = 8;
const PREFIX = 'SOKDAQ';

/**
 * 코드를 만든다.
 *
 * 난수는 주입받는다. Workers에서는 crypto.getRandomValues를 넘긴다.
 * Math.random()으로는 절대 만들지 않는다 — 이 코드가 방의 유일한 접근 수단이다.
 */
export function generateInviteCode(
  fillRandom: (bytes: Uint8Array) => void = defaultFillRandom,
): string {
  const chars: string[] = [];

  // 32는 256을 나누어떨어지게 하므로 바이트를 그대로 %32 해도
  // 모듈로 편향이 생기지 않는다.
  const bytes = new Uint8Array(CODE_LENGTH);
  fillRandom(bytes);

  for (const byte of bytes) {
    chars.push(ALPHABET[byte % ALPHABET.length]!);
  }

  return `${PREFIX}-${chars.slice(0, 4).join('')}-${chars.slice(4).join('')}`;
}

/**
 * WebCrypto의 최소 지면만 선언한다.
 *
 * DOM이나 @types/node를 lib에 끌어오면 이 패키지가 특정 런타임에 묶인다.
 * room-core는 Workers·Node·브라우저 어디서든 그대로 돌아야 한다.
 */
interface RandomSource {
  getRandomValues(array: Uint8Array): Uint8Array;
}

function defaultFillRandom(bytes: Uint8Array): void {
  const source = (globalThis as { crypto?: RandomSource }).crypto;
  if (typeof source?.getRandomValues !== 'function') {
    throw new Error('crypto.getRandomValues를 쓸 수 없습니다. 난수 생성기를 주입하세요.');
  }
  source.getRandomValues(bytes);
}

/**
 * 사람이 입력한 코드를 표준형으로 되돌린다.
 *
 * Crockford 관용 규칙을 따라 오타를 흡수한다:
 * 소문자 → 대문자, I·L → 1, O → 0, 하이픈·공백 무시.
 * 접두사는 생략해도 받는다.
 *
 * 형식이 틀리면 null. 절대 throw하지 않는다.
 */
export function normalizeInviteCode(input: string): string | null {
  const cleaned = input.toUpperCase().replace(/[\s-]/g, '');

  // 접두사를 먼저 떼어낸다. Crockford 치환보다 뒤로 미루면 'SOKDAQ'의 O가
  // 0으로 바뀌어 접두사를 알아보지 못한다.
  //
  // 길이까지 맞을 때만 접두사로 인정한다. 'SOKDAQ12'처럼 접두사로 시작하는
  // 8자 본문과 구별할 방법이 그것뿐이다.
  const body =
    cleaned.length === PREFIX.length + CODE_LENGTH && cleaned.startsWith(PREFIX)
      ? cleaned.slice(PREFIX.length)
      : cleaned;

  const substituted = body.replace(/[IL]/g, '1').replace(/O/g, '0');

  if (substituted.length !== CODE_LENGTH) return null;
  for (const ch of substituted) {
    if (!ALPHABET.includes(ch)) return null;
  }

  return `${PREFIX}-${substituted.slice(0, 4)}-${substituted.slice(4)}`;
}
